
import "@/sass/globals.scss";

import Vue from "vue";
import { Component, Prop } from "vue-property-decorator";
import defensiveCopy from "@/util/defensive-copy";
import ConditionsMatrix from "@/components/Reusable/CustomInputs/Conditions/ConditionsMatrix.vue";

import DropdownInput from "@/components/Reusable/CustomInputs/DropdownInput.vue";
import TextInput from "@/components/Reusable/CustomInputs/TextInput.vue";
import NumberInput from "@/components/Reusable/CustomInputs/NumberInput.vue";
import lodash from "lodash";
import {
  removeChangesNotSavedWarning,
  warnIfExitingWithUnsavedChanges
} from "@/util/warn-unsaved-changes";
import { pipe } from "fp-ts/lib/function";
import { fold } from "fp-ts/lib/Either";
import NotificationLogger from "@/lib/notifications/notifications";
import {
  getBlocksAndPutIntoStore,
  saveGlobalBlockToWordPress
} from "@/lib/global-blocks/global-blocks";
import { centerDialogInScreen } from "@/subapps/manage-templates/lib/modal-position";
import { GlobalBlock } from "@/types/global-blocks";
@Component({
  components: {
    NumberInput,
    TextInput,
    DropdownInput,
    ConditionsMatrix
  }
})
export default class EditGlobalBlockModal extends Vue {
  @Prop()
  value!: GlobalBlock;

  @Prop()
  mode!: "add" | "edit";

  @Prop()
  globalBlocks!: GlobalBlock[];

  internalValue: GlobalBlock = defensiveCopy(this.value);

  warnIfUnsavedFunction!: (e: Event) => void;

  saveInProgress = false;

  mounted() {
    centerDialogInScreen();

    this.warnIfUnsavedFunction = warnIfExitingWithUnsavedChanges(
      () => this.hasUnsavedChanges
    );
  }

  setLabel(newTitle: string | null) {
    this.internalValue.label = newTitle || "";
  }

  get hasUnsavedChanges() {
    return !lodash.isEqual(this.internalValue, this.value);
  }

  closeDialog() {
    removeChangesNotSavedWarning(this.warnIfUnsavedFunction);

    this.$emit("close");
  }

  async saveGlobalBlock() {
    if (!this.internalValue.label) {
      NotificationLogger.log.message("Title field can't be empty");
      return;
    }

    const existingGlobalBlock = this.globalBlocks.find(
      block =>
        block.label === this.internalValue.label &&
        block.id !== this.internalValue.id
    );

    // The only thing differentiating global blocks when selecting them is
    // the title. Having two equal titles is super confusing
    if (existingGlobalBlock) {
      NotificationLogger.log.message(
        `A ${this.$bdox("global_block")} with the title "${
          this.internalValue.label
        }" already exists. Please select another title.`
      );
      return;
    }

    pipe(
      await saveGlobalBlockToWordPress(this.internalValue),
      fold(
        () => {
          NotificationLogger.log.message(
            `${this.$bdox("global_block")} was not saved`
          );
        },
        savedBlock => {
          this.$emit("input", savedBlock);

          this.closeDialog();
        }
      )
    );

    getBlocksAndPutIntoStore();
  }
}
